import { z } from 'zod';
import { getJson, sendJson } from '@/shared/api/client';
import { pageSchema } from '@/shared/api/page';

export const subscriberListSchema = z.object({
  id: z.string(),
  name: z.string(),
  kind: z.enum(['topic', 'client_book']),
  ownerName: z.string().nullable(),
  /** The RM account that owns a client list. */
  ownerId: z.number().int().nullable().default(null),
  /** One line on what the list receives, e.g. "Private-banking clients, Singapore". */
  description: z.string().nullish().default(null),
  count: z.number().int(),
  /** Of count, those who will receive messages. */
  optedInCount: z.number().int().default(0),
  /** Opted-in names with masked numbers, the first few; count covers the rest. */
  recipients: z.array(z.object({ name: z.string(), phoneMasked: z.string() })).default([]),
  /** This account may change who is on it: admins, and an RM for their own lists. */
  editable: z.boolean().default(false),
});
export type SubscriberListRef = z.infer<typeof subscriberListSchema>;

export const subscriberSchema = z.object({
  id: z.string(),
  listId: z.string(),
  name: z.string(),
  phoneMasked: z.string(),
  emailMasked: z.string().nullable().default(null),
  optedIn: z.boolean(),
  optedInAt: z.string().nullable(),
});
export type Subscriber = z.infer<typeof subscriberSchema>;

export type SubscriberParams = { page: number; pageSize: number; listId: string; q?: string };

/** The lists this account may send to; `mine` keeps only the ones it manages (an RM's own client lists). */
export const fetchSubscriberLists = (signal?: AbortSignal, mine = false) =>
  getJson('/subscriber-lists', z.array(subscriberListSchema), { ...(mine ? { params: { mine: true } } : {}), ...(signal ? { signal } : {}) });

export type NewList = { name: string; description: string; ownerId?: number | undefined };

/** A new list: the RM's own, a shared one when an admin makes it, or (admins, with ownerId) one for that RM. */
export const createList = ({ name, description, ownerId }: NewList) =>
  sendJson('post', '/subscriber-lists', subscriberListSchema, { name, description, ...(ownerId === undefined ? {} : { ownerId }) });

/** Rename a list and set its description; an empty description clears it. */
export const renameList = ({ id, name, description }: { id: string; name: string; description: string }) =>
  sendJson('patch', `/subscriber-lists/${id}`, subscriberListSchema, { name, description });

/** Relationship managers, for an admin choosing who owns a new client book. */
export const fetchRms = (signal?: AbortSignal) =>
  getJson('/users', pageSchema(z.object({ id: z.number().int(), name: z.string() })), { params: { role: 'rm', page: 1, pageSize: 100 }, ...(signal ? { signal } : {}) });

/** A client from the RM's own file, by its line (the header is line 1). */
const importedRowSchema = z.object({ row: z.number().int(), name: z.string(), phone: z.string(), email: z.string() });

export const importResultSchema = z.object({
  added: z.number().int(),
  /** The clients added, as stored. */
  imported: z.array(importedRowSchema),
  /** Rows not added, exactly as written in the file, with the reason. */
  problems: z.array(importedRowSchema.extend({ reason: z.string() })),
});
export type ImportResult = z.infer<typeof importResultSchema>;

/** Clients from a CSV with name, phone and email columns. optedIn: everyone in the file agreed to WhatsApp updates. */
export const importClients = ({ listId, csv, optedIn }: { listId: string; csv: string; optedIn: boolean }) =>
  sendJson('post', `/subscriber-lists/${listId}/import`, importResultSchema, { csv, optedIn });

export const deleteList = (id: string) => sendJson('delete', `/subscriber-lists/${id}`, z.object({ id: z.string() }));

export const fetchSubscribers = (params: SubscriberParams, signal?: AbortSignal) =>
  getJson('/subscribers', pageSchema(subscriberSchema), { params, ...(signal ? { signal } : {}) });

export type NewSubscriber = { listId: string; name: string; phone: string; email: string | null; optedIn: boolean };

export const addSubscriber = (body: NewSubscriber) =>
  sendJson('post', '/subscribers', subscriberSchema, body);

export const removeSubscriber = (id: string) => sendJson('delete', `/subscribers/${id}`, z.object({ id: z.string() }));
